import { artistHref, similarHref } from '@/features/artists/artistHref'
import { ChevronDown, Sparkles } from 'lucide-react'
import { Dialog as Primitive } from 'radix-ui'
import { Link, useNavigate } from 'react-router'

import { usePlayer } from '@/audio/store'
import { IconButton, useEnterTransition } from '@/components/ui'
import { cn } from '@/lib/cn'

import { CurrentSongActions } from './CurrentSongActions'
import { PlayerArtwork } from './PlayerArtwork'
import { QueueClearButton, QueueContents } from './QueueContents'
import { QueueFeedback } from './QueueFeedback'
import { usePlayerGestures } from './usePlayerGestures'
import { SeekBar } from './SeekBar'
import {
  NextButton,
  PlayPauseButton,
  PreviousButton,
  RepeatButton,
  ShuffleButton,
} from './TransportControls'

export interface FullScreenPlayerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  returnFocus: () => void
}

/**
 * The expanded "now playing" surface.
 *
 * Built on the Dialog primitive rather than the shared `Sheet`, because this one
 * covers the whole viewport at every breakpoint — `Sheet` deliberately docks to
 * the right at md: and up.
 */
export function FullScreenPlayer({ open, onOpenChange, returnFocus }: FullScreenPlayerProps) {
  const navigate = useNavigate()
  const queue = usePlayer((state) => state.queue)
  const index = usePlayer((state) => state.index)
  const isBuffering = usePlayer((state) => state.isBuffering)
  const waiting = usePlayer((state) => state.awaitingContinuation)
  const error = usePlayer((state) => state.error)

  const entered = useEnterTransition(open)
  const gestures = usePlayerGestures(open, () => onOpenChange(false))

  const current = index >= 0 && index < queue.length ? queue[index] : null
  if (!current) return null

  const status = error ?? (waiting ? 'Getting a similar song ready…' : isBuffering ? 'Buffering…' : current.suggested ? 'Playing a similar song' : '')

  return (
    <Primitive.Root open={open} onOpenChange={onOpenChange}>
      <Primitive.Portal>
        <Primitive.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-overlay transition-opacity duration-200',
            entered ? 'opacity-100' : 'opacity-0',
          )}
        />
        <Primitive.Content
          ref={gestures.ref}
          onCloseAutoFocus={(event) => { event.preventDefault(); returnFocus() }}
          data-player-view
          style={gestures.offset.y ? { transform: `translateY(${gestures.offset.y}px)`, transition: 'none' } : undefined}
          aria-describedby={undefined}
          className={cn(
            'fixed inset-0 z-50 flex flex-col bg-surface-raised',
            // A fixed element escapes the safe-area padding <body> carries.
            'pt-safe',
            'transition-transform duration-200 ease-out motion-reduce:transition-none',
            entered ? 'translate-y-0' : 'translate-y-full',
          )}
        >
          <div data-player-header className="flex select-none items-center gap-2 px-3 py-2" style={{ WebkitTouchCallout: 'none' }}>
            <Primitive.Close asChild>
              <IconButton aria-label="Close full screen player">
                <ChevronDown className="h-6 w-6" aria-hidden="true" />
              </IconButton>
            </Primitive.Close>
            <Primitive.Title className="min-w-0 flex-1 truncate text-center text-xs font-semibold uppercase tracking-wide text-text-muted">
              Now playing
            </Primitive.Title>
            {/* The moment you are most likely to want more of what you are
                hearing is while you are hearing it, so the way out to the
                neighbours lives here rather than three screens away. It also
                keeps the title optically centred against the close button — the
                spacer this replaced did nothing else. */}
            {current.localArtistId || current.artistId ? (
              <Primitive.Close asChild>
                <IconButton
                  aria-label={`More music like ${current.artistName}`}
                  onClick={() => void navigate(similarHref({ local_artist_id: current.localArtistId, artist_id: current.artistId }))}
                >
                  <Sparkles className="h-5 w-5" aria-hidden="true" />
                </IconButton>
              </Primitive.Close>
            ) : (
              <span aria-hidden="true" className="h-11 w-11 shrink-0" />
            )}
          </div>

          <div data-player-scroll className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div className="grid w-full grid-cols-1 pb-6 md:grid-cols-[minmax(16rem,min(40vw,28rem,calc(100dvh-22rem)))_minmax(0,1fr)] md:items-start md:gap-8 md:pl-8 lg:gap-12 lg:pl-10">
              <div className="mx-auto w-full min-w-0 max-w-md px-5 md:sticky md:top-0 md:px-0 md:py-4" data-player-controls>
                <PlayerArtwork key={current.uid} item={current} offset={gestures.offset} />

                <div className="mt-4 flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h2 className="min-w-0 truncate text-xl font-semibold text-text">{current.title}</h2>
                    <p className="truncate text-base text-text-muted">
                      {current.localArtistId || current.artistId ? (
                        <Primitive.Close asChild>
                          <Link to={artistHref({ local_artist_id: current.localArtistId, artist_id: current.artistId })!} className="inline-flex min-h-11 max-w-full items-center rounded-md hover:text-text hover:underline underline-offset-4">
                            <span className="truncate">{current.artistName}</span>
                          </Link>
                        </Primitive.Close>
                      ) : current.artistName}
                    </p>
                    {current.albumName ? <p className="truncate text-sm text-text-subtle">{current.albumName}</p> : null}
                  </div>
                  <CurrentSongActions key={current.uid} item={current} active={open} />
                </div>

                {/* Fixed height: the line appears and disappears without moving the
                    transport controls under it. */}
                <p
                  aria-live="polite"
                  className={cn(
                    'mt-2 h-5 truncate text-sm',
                    error ? 'text-danger' : 'text-text-muted',
                  )}
                >
                  {status}
                </p>

                <SeekBar className="mt-1" />

                <div className="mt-3 flex items-center justify-between gap-1">
                  <ShuffleButton size="md" />
                  <PreviousButton size="lg" iconClassName="h-7 w-7" />
                  <PlayPauseButton size="lg" className="h-16 w-16" iconClassName="h-7 w-7" />
                  <NextButton size="lg" iconClassName="h-7 w-7" />
                  <RepeatButton size="md" />
                </div>
              </div>

              <section className="mt-8 min-w-0 pb-2 md:mt-4" aria-labelledby="fullscreen-queue">
                <div className="flex items-start gap-2 px-4 pb-3 pt-3">
                  <div className="min-w-0 flex-1">
                    <h3 id="fullscreen-queue" className="text-base font-semibold text-text">Queue</h3>
                    <p className="mt-0.5 text-sm text-text-muted">{queue.length === 1 ? '1 song' : `${queue.length} songs`}</p>
                  </div>
                  <QueueClearButton onBeforeClear={() => onOpenChange(false)} />
                </div>
                <div className="px-2">
                  <QueueContents />
                </div>
              </section>
            </div>
            <div aria-hidden="true" className="safe-bottom" />
          </div>
          <QueueFeedback className="safe-bottom mx-3 mb-2 shrink-0" />
        </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  )
}
